/**
 * HelpDrawer — persistent right-side drawer accessed from the TopBar "?" button.
 *
 * Phase 8 Prompt 2 — per audit deliverable 04B Section I Help Drawer Content
 * Spec. Sections:
 *   1. On this screen — surface-specific how-to (sourced by current route)
 *   2. Common tasks — top tasks for current surface
 *   3. Glossary — searchable; 19 seeded terms
 *   4. What's new — feed of platform updates
 *
 * The drawer-open state is owned by HelpContentContext so the TopBar "?"
 * button and any in-page "Learn more" link can both open it.
 *
 * Usage (mounted once at app root via <HelpDrawerMount/>):
 *   <HelpDrawerMount />
 */

import React, { useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  TextField,
  Tabs,
  Tab,
  Chip,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  useHelpContent,
  GlossaryEntry,
} from "../../contexts/HelpContentContext";

const DRAWER_WIDTH = 400;

// ---------------------------------------------------------------------------
// Map route paths → surface IDs the help registry knows about. Allows the
// "On this screen" + "Common tasks" sections to auto-render the right content.
// ---------------------------------------------------------------------------
function resolveSurfaceFromPath(pathname: string): string {
  // Order matters — most specific first.
  if (pathname.startsWith("/dashboard/communications/marketing/funnels"))
    return "funnels";
  if (pathname.startsWith("/dashboard/communications/marketing/campaigns"))
    return "campaigns";
  if (pathname.startsWith("/dashboard/communications/marketing/audiences"))
    return "audiences";
  if (pathname.startsWith("/dashboard/communications/operate/free-tier"))
    return "freetier";
  if (pathname.startsWith("/dashboard/communications"))
    return "communications.overview";
  if (pathname.startsWith("/dashboard/free-tier")) return "freetier";
  return "communications.overview";
}

// ---------------------------------------------------------------------------
// Glossary entry display row
// ---------------------------------------------------------------------------
const GlossaryRow: React.FC<{ entry: GlossaryEntry }> = ({ entry }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="subtitle2"
      sx={{
        color: "#1A5F7A",
        fontWeight: 600,
        fontFamily: '"Playfair Display", serif',
      }}
    >
      {entry.term}
    </Typography>
    <Typography variant="body2" sx={{ color: "text.primary", lineHeight: 1.5 }}>
      {entry.definition}
    </Typography>
    {entry.whyThisMatters && (
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          display: "block",
          mt: 0.5,
          fontStyle: "italic",
        }}
      >
        Why this matters: {entry.whyThisMatters}
      </Typography>
    )}
    {entry.related && entry.related.length > 0 && (
      <Box sx={{ mt: 0.8, display: "flex", flexWrap: "wrap", gap: 0.4 }}>
        {entry.related.map((r) => (
          <Chip
            key={r}
            label={r}
            size="small"
            sx={{
              fontSize: "0.65rem",
              height: 20,
              bgcolor: "#F9F3E9",
              color: "#1A5F7A",
            }}
          />
        ))}
      </Box>
    )}
  </Box>
);

// ---------------------------------------------------------------------------
// Tab Panel helper
// ---------------------------------------------------------------------------
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    sx={{ pt: 2 }}
    aria-labelledby={`help-tab-${index}`}
  >
    {value === index && children}
  </Box>
);

// ---------------------------------------------------------------------------
// HelpDrawerMount — the actual drawer wired to context state.
// Mount once in the app root.
// ---------------------------------------------------------------------------
export const HelpDrawerMount: React.FC = () => {
  const { drawer, closeDrawer, content, searchGlossary, getCommonTasks } =
    useHelpContent();
  const location = useLocation();

  const [tab, setTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  // Resolve the current surface for the "On this screen" section.
  const surfaceKey = drawer.surfaceContext ?? resolveSurfaceFromPath(location.pathname);
  const commonTasks = getCommonTasks(surfaceKey);

  // Filtered glossary
  const glossaryEntries = useMemo(
    () => searchGlossary(searchQuery),
    [searchQuery, searchGlossary],
  );

  return (
    <Drawer
      anchor="right"
      open={drawer.open}
      onClose={closeDrawer}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: DRAWER_WIDTH },
          maxWidth: "100vw",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: '"Playfair Display", serif',
            fontWeight: 700,
            color: "#1A5F7A",
          }}
        >
          Help
        </Typography>
        <IconButton onClick={closeDrawer} size="small" aria-label="Close help">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="fullWidth"
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          "& .MuiTab-root": {
            fontFamily: '"Montserrat", sans-serif',
            fontSize: "0.75rem",
            fontWeight: 600,
            textTransform: "uppercase",
            minHeight: 44,
          },
          "& .Mui-selected": { color: "#1A5F7A" },
          "& .MuiTabs-indicator": { backgroundColor: "#D4AC0D" },
        }}
      >
        <Tab label="This screen" id="help-tab-0" />
        <Tab label="Glossary" id="help-tab-1" />
        <Tab label="What's new" id="help-tab-2" />
      </Tabs>

      <Box sx={{ p: 2, overflowY: "auto", flex: 1 }}>
        {/* ───── Tab 0: This screen ───── */}
        <TabPanel value={tab} index={0}>
          <Typography
            variant="overline"
            sx={{ color: "text.secondary", fontWeight: 700 }}
          >
            On this screen
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 0.5, mb: 2, lineHeight: 1.6 }}
          >
            {surfaceKey === "communications.overview" &&
              "The Communications Overview is your top-line dashboard across both pillars (Transactional and Marketing & Nurture). Spot anomalies, see what's about to send, and drill into any surface."}
            {surfaceKey === "funnels" &&
              "Funnels are automated multi-step nurture sequences. Pause/resume one-click on every row. Click into any funnel to see its canvas, step health, and live enrollment counts."}
            {surfaceKey === "campaigns" &&
              "Campaigns are one-off broadcast sends. Use the 4-step wizard (Audience → Content → Schedule → Review). Test before sending broadcast."}
            {surfaceKey === "audiences" &&
              "Audiences are saved groups of members based on conditions. Reuse across campaigns and funnels. Live count updates as you build."}
            {surfaceKey === "freetier" &&
              "The Free Tier Conversion Engine shows live cohort flows. Use it to spot Hot Leads and send targeted promotions."}
            {!["communications.overview", "funnels", "campaigns", "audiences", "freetier"].includes(
              surfaceKey,
            ) &&
              "Use the navigation to access communications, content, and analytics surfaces. Help is available on every screen via the ? button up top."}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography
            variant="overline"
            sx={{ color: "text.secondary", fontWeight: 700 }}
          >
            Common tasks
          </Typography>
          <Box component="ul" sx={{ mt: 0.5, mb: 2, pl: 2.5 }}>
            {commonTasks.length === 0 && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                No tasks documented for this surface yet.
              </Typography>
            )}
            {commonTasks.map((task, i) => (
              <Typography
                key={i}
                component="li"
                variant="body2"
                sx={{ mb: 0.6, lineHeight: 1.5 }}
              >
                {task}
              </Typography>
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography
            variant="overline"
            sx={{ color: "text.secondary", fontWeight: 700 }}
          >
            Need more help?
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Email{" "}
            <a
              href="mailto:support@trylifescribe.com"
              style={{ color: "#1A5F7A" }}
            >
              support@trylifescribe.com
            </a>
          </Typography>
        </TabPanel>

        {/* ───── Tab 1: Glossary ───── */}
        <TabPanel value={tab} index={1}>
          <TextField
            fullWidth
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`Search ${content.glossary.length}+ terms…`}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {glossaryEntries.length === 0 && (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", textAlign: "center", py: 3 }}
            >
              No glossary entries match &ldquo;{searchQuery}&rdquo;.
            </Typography>
          )}

          {glossaryEntries.map((entry) => (
            <GlossaryRow key={entry.term} entry={entry} />
          ))}
        </TabPanel>

        {/* ───── Tab 2: What's new ───── */}
        <TabPanel value={tab} index={2}>
          <Typography
            variant="overline"
            sx={{ color: "text.secondary", fontWeight: 700 }}
          >
            Platform updates
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 600 }}
              >
                May 2026
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.3, lineHeight: 1.5 }}>
                Communications Platform unification — two-pillar IA
                (Transactional + Marketing & Nurture). Help system available on
                every screen. <em>You are here.</em>
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 600 }}
              >
                April 2026
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.3, lineHeight: 1.5 }}>
                Free Tier Conversion Engine v1 deployed. Real-time SSE event
                ticker. Cart abandonment recovery flow live.
              </Typography>
            </Box>
          </Box>
        </TabPanel>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          textAlign: "center",
          color: "text.secondary",
          fontSize: "0.7rem",
        }}
      >
        Help registry v{content.version} · Updated {content.lastUpdated}
      </Box>
    </Drawer>
  );
};

export default HelpDrawerMount;
